import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import type { CommandItem } from "./commandItem";
import { CommandPaletteFooter } from "./CommandPaletteFooter";
import { CommandPaletteResults } from "./CommandPaletteResults";
import { CommandPaletteSearch } from "./CommandPaletteSearch";
import { paletteKeyDownHandler } from "./paletteKeyboard";
import { commandOptionId } from "./paletteModel";
import { usePaletteFocus } from "./usePaletteFocus";
import { usePaletteResults } from "./usePaletteResults";

export type { CommandItem } from "./commandItem";
export { filterCommands } from "./filterCommands";
export { isApplePlatform, isEditableTarget, isPaletteShortcut } from "./paletteShortcut";

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
  placeholder?: string;
  /** Text to start with (e.g. the first key typed on the header search field). */
  initialQuery?: string;
}

const DIALOG_LABEL = "Search the suite";

/** Client-side command palette: search navigation targets and quick actions. ↑ ↓ Enter Esc. */
export function CommandPalette({ open, onClose, items, placeholder = DIALOG_LABEL, initialQuery = "" }: CommandPaletteProps): React.JSX.Element | null {
  const { query, setQuery, results, active, setActive, listRef } = usePaletteResults(items, open, initialQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  usePaletteFocus(open, inputRef);

  if (!open) return null;

  function run(item: CommandItem | undefined): void {
    if (!item) return;
    onClose();
    item.onSelect();
  }

  const activeItem = results[active];

  return createPortal(
    <div
      className="nb-modal-back nb-palette-back"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="nb-palette"
        role="dialog"
        aria-modal="true"
        aria-label={DIALOG_LABEL}
        onKeyDown={paletteKeyDownHandler({ results, active, setActive, run, onClose })}
      >
        <CommandPaletteSearch
          inputRef={inputRef}
          listId={listId}
          activeOptionId={activeItem ? commandOptionId(listId, activeItem) : undefined}
          placeholder={placeholder}
          query={query}
          onQueryChange={setQuery}
        />
        <CommandPaletteResults listId={listId} listRef={listRef} results={results} query={query} active={active} onActivate={setActive} onRun={run} />
        <CommandPaletteFooter />
      </div>
    </div>,
    document.body,
  );
}
